// Lifted-parcel ascent from the surface, ground up. Returns the (T, p) curve
// of a parcel lifted dry-adiabatically until LCL, then moist-adiabatically
// from LCL up to the top of the atmosphere.
//
// Algorithm mirrors Skysight's bundle (1050 → 100 hPa in 0.1-hPa steps).

import { getTlcl, getPlcl } from './lcl.js';
import { wetAdiabaticSlope } from './moist.js';

const POISSON = 2 / 7;
const PRESSURE_TOP = 100;        // hPa, top of curve
const PRESSURE_START = 1050;     // hPa, back-extrapolation start (to handle sites above 1000 hPa)
const STEP = 0.1;                // hPa, integration step
const HALF_STEP = STEP / 2;

/**
 * Lift a surface parcel through the atmosphere.
 *
 * @param {number} t0    - surface temperature, °C
 * @param {number} td0   - surface dewpoint, °C
 * @param {number} p0    - surface pressure, hPa
 * @returns {{path: Array<{t:number, p:number}>, tlcl: number, plcl: number}}
 */
export function liftParcel(t0, td0, p0) {
  const tlcl = getTlcl(t0, td0);
  const plcl = getPlcl(p0, t0, tlcl);

  const path = [];
  // Start at 1050 hPa using θ-conservation (back-extrapolation from p0)
  let T = (t0 + 273.15) * Math.pow(PRESSURE_START / p0, POISSON) - 273.15;

  for (let p = PRESSURE_START; p > PRESSURE_TOP; p -= STEP) {
    if (p >= plcl) {
      // Below LCL: dry adiabat, θ-preserving
      T = (t0 + 273.15) * Math.pow(p / p0, POISSON) - 273.15;
    } else {
      // Above LCL: moist adiabat, numerical integration with midpoint slope.
      // wetAdiabaticSlope returns °C/hPa (p input is hPa), so multiply by
      // STEP (hPa) directly — no extra unit factor needed.
      T = T - wetAdiabaticSlope(T, p - HALF_STEP) * STEP;
    }
    path.push({ t: T, p });
  }

  return { path, tlcl, plcl };
}
